import * as React from "react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ModelPartnerPartner } from "@dia-ui/util_interlink-api";
import { AppContext } from "../../context/AppContext";
import IconComponent from "../IconComponent";
import InlineMessage, { InlineMessageState } from "../InlineMessage";

interface PartnerConfigFormProps {
  partner: ModelPartnerPartner;
  /**
   * only the supplier of the relation may edit; the customer gets the same
   * mask read-only (the backend enforces the same rule with a 403)
   */
  editable: boolean;
  onSaved?: (updated: ModelPartnerPartner) => void;
}

/**
 * one catalog entry from GET /system/enum/plugins: the plugin classes
 * describe themselves (descriptions and required data parameters)
 */
interface PluginCatalogEntry {
  name: string;
  type: string;
  interlinkModulType: string;
  descriptionShort: string;
  descriptionLong: string;
  parameter: string[];
}

/**
 * one configured plugin while editing. `values` holds the parameters the
 * catalog lists (editable, as strings), `hidden` everything else from the
 * stored data (preserved untouched), `original` the raw values at load so an
 * unchanged field round-trips without a type change
 */
interface PluginEntry {
  name: string;
  type: string;
  values: Record<string, string>;
  hidden: Record<string, unknown>;
  original: Record<string, unknown>;
}

/**
 * one config module while editing. `data` carries everything except
 * apiParameter and plugins verbatim (state, enabled flags, unknown fields).
 * apiParameter is restricted to the list the supplier's api class announces:
 * `apiValues` are those editable fields, `apiHidden` keeps unlisted stored
 * parameters invisible but intact
 */
interface ModuleEntry {
  id: string;
  data: Record<string, any>;
  apiValues: Record<string, string>;
  apiOriginal: Record<string, unknown>;
  apiHidden: Record<string, unknown>;
  hasApiParameter: boolean;
  hasPlugins: boolean;
  plugins: PluginEntry[];
}

/**
 * modules that carry apiParameter and plugins; passthrough modules like
 * partnerFile only have a state
 */
const TYPED_MODULES = ["priceAndStock", "productSearch", "bulkStockExport"];

/**
 * PartnerConfigForm
 *
 * structured editor for the partner_partners config column: module state,
 * enabled flags, the api parameters the supplier's api class lists, and the
 * plugins (add/remove/edit with the fields the plugin classes announce).
 * unknown config nodes and unlisted parameters survive saving untouched.
 */
const PartnerConfigForm: React.FC<PartnerConfigFormProps> = ({ partner, editable, onSaved }) => {
  const {
    services: {
      servicePartnerPartner
    },
    I18N
  } = useContext(AppContext);

  /**
   * enum data cached by SystemService at login, same source as ModuleTypes.
   * the backend enum descriptions may carry a stray degree sign, stripped
   * before display
   */
  const moduleTypeEnum = useMemo(() => {
    return Object.values(
      JSON.parse(localStorage.getItem("dia:service:system:interlinkModulTypes") || "{}") as Record<string, any>
    ).filter((_type: any) => !!_type?.id);
  }, []);

  const statusEnum = useMemo(() => {
    return Object.values(
      JSON.parse(localStorage.getItem("dia:service:system:partnerPartnerStatus") || "{}") as Record<string, any>
    ).filter((_state: any) => !!_state?.id);
  }, []);

  const pluginTypeEnum = useMemo(() => {
    return Object.values(
      JSON.parse(localStorage.getItem("dia:service:system:pluginTypes") || "{}") as Record<string, any>
    ).filter((_type: any) => !!_type?.id);
  }, []);

  const cleanLabel = useCallback((label: unknown, fallback: string): string => {
    return String(label || fallback).replace(/°/g, "").trim();
  }, []);

  const moduleLabel = useCallback((id: string): string => {
    const match: any = moduleTypeEnum.find((_type: any) => _type.id === id);
    return I18N.t(cleanLabel(match?.description, id));
  }, [moduleTypeEnum, cleanLabel, I18N]);

  const pluginTypeLabel = useCallback((id: string): string => {
    const match: any = pluginTypeEnum.find((_type: any) => _type.id === id);
    return I18N.t(cleanLabel(match?.description, id));
  }, [pluginTypeEnum, cleanLabel, I18N]);

  /**
   * state variables
   */
  const [modules, setModules] = useState<ModuleEntry[]>([]);
  const [pluginCatalog, setPluginCatalog] = useState<PluginCatalogEntry[]>([]);
  const [apiParameterList, setApiParameterList] = useState<Record<string, string[]>>({});
  const [loaded, setLoaded] = useState<boolean>(false);
  const [moduleToAdd, setModuleToAdd] = useState<string>("");
  const [pluginToAdd, setPluginToAdd] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<boolean>(false);
  const [dirty, setDirty] = useState<boolean>(false);
  const [message, setMessage] = useState<InlineMessageState | null>(null);

  /**
   * catalogEntry
   * @param type
   * @param name
   */
  const catalogEntry = useCallback((type: string, name: string): PluginCatalogEntry | undefined => {
    return pluginCatalog.find((entry) => entry.type === type && entry.name === name);
  }, [pluginCatalog]);

  /**
   * splitByList
   * separates an object into the keys a list announces (as string values for
   * the inputs) and the rest (preserved invisibly)
   * @param raw
   * @param listed
   */
  const splitByList = useCallback((raw: Record<string, unknown>, listed: string[]) => {
    const values: Record<string, string> = {},
      hidden: Record<string, unknown> = {};

    listed.forEach((key) => {
      values[key] = raw[key] !== undefined && raw[key] !== null ? String(raw[key]) : "";
    });
    Object.keys(raw).forEach((key) => {
      if (!listed.includes(key)) {
        hidden[key] = raw[key];
      }
    });

    return { values, hidden };
  }, []);

  /**
   * buildModules
   * normalizes the raw config into editable entries. PHP serializes empty
   * config nodes as [] instead of {}, so arrays are treated as empty objects
   * @param rawConfig
   * @param catalog
   * @param apiLists
   */
  const buildModules = useCallback((
    rawConfig: any,
    catalog: PluginCatalogEntry[],
    apiLists: Record<string, string[]>
  ): ModuleEntry[] => {
    const config = rawConfig && typeof rawConfig === "object" && !Array.isArray(rawConfig) ? rawConfig : {};

    return Object.keys(config).map((id) => {
      const rawModule = config[id],
        moduleData = rawModule && typeof rawModule === "object" && !Array.isArray(rawModule)
          ? JSON.parse(JSON.stringify(rawModule))
          : {},
        hasApiParameter = Object.prototype.hasOwnProperty.call(moduleData, "apiParameter"),
        hasPlugins = Object.prototype.hasOwnProperty.call(moduleData, "plugins"),
        rawApiParameter = moduleData.apiParameter && typeof moduleData.apiParameter === "object" && !Array.isArray(moduleData.apiParameter)
          ? moduleData.apiParameter
          : {},
        rawPlugins: any[] = Array.isArray(moduleData.plugins) ? moduleData.plugins : [];

      delete moduleData.apiParameter;
      delete moduleData.plugins;

      const { values: apiValues, hidden: apiHidden } = splitByList(rawApiParameter, apiLists[id] || []);

      const plugins: PluginEntry[] = rawPlugins
        .filter((plugin) => plugin && typeof plugin === "object")
        .map((plugin) => {
          const rawData = plugin.data && typeof plugin.data === "object" && !Array.isArray(plugin.data) ? plugin.data : {},
            listed = catalog.find((entry) => entry.type === plugin.type && entry.name === plugin.name)?.parameter
              // a plugin the catalog does not know keeps its stored fields editable
              ?? Object.keys(rawData),
            { values, hidden } = splitByList(rawData, listed);

          return {
            name: String(plugin.name || ""),
            type: String(plugin.type || ""),
            values,
            hidden,
            original: rawData
          };
        });

      return {
        id,
        data: moduleData,
        apiValues,
        apiOriginal: rawApiParameter,
        apiHidden,
        hasApiParameter,
        hasPlugins,
        plugins
      };
    });
  }, [splitByList]);

  /**
   * load plugin catalog and the api parameter list of this relation, then
   * build the editable entries; the mask waits for both so the field split
   * (listed vs. preserved) is stable
   */
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [catalog, apiLists] = await Promise.all([
          servicePartnerPartner.getPluginCatalog(),
          servicePartnerPartner.getApiParameterList(partner.id)
        ]);
        if (cancelled) {
          return;
        }
        setPluginCatalog(catalog || []);
        setApiParameterList(apiLists || {});
        setModules(buildModules(partner?.config, catalog || [], apiLists || {}));
        setDirty(false);
        setLoaded(true);
      } catch (error) {
        if (!cancelled) {
          setMessage({
            kind: "error",
            text: I18N.t("Die Konfigurationsdaten konnten nicht geladen werden.")
          });
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [partner, servicePartnerPartner, buildModules, I18N]);

  /**
   * mergeWithOriginals
   * folds string inputs back over the raw values: an unchanged field keeps
   * its original raw value (numbers survive), an edited one is saved as
   * string; empty fields are dropped
   * @param values
   * @param original
   */
  const mergeWithOriginals = useCallback((values: Record<string, string>, original: Record<string, unknown>): Record<string, unknown> => {
    const result: Record<string, unknown> = {};

    Object.keys(values).forEach((key) => {
      const value = values[key];
      if (value === "") {
        return;
      }
      result[key] = original[key] !== undefined && String(original[key] ?? "") === value
        ? original[key]
        : value;
    });

    return result;
  }, []);

  /**
   * composeConfig
   * folds the editable entries back into the plain config object that is
   * sent to the backend (and mirrored in the JSON preview)
   */
  const composeConfig = useCallback((_modules: ModuleEntry[]): Record<string, any> => {
    const config: Record<string, any> = {};

    _modules.forEach((module) => {
      const data = JSON.parse(JSON.stringify(module.data));

      const apiParameter = {
        ...module.apiHidden,
        ...mergeWithOriginals(module.apiValues, module.apiOriginal)
      };
      if (module.hasApiParameter || Object.keys(apiParameter).length) {
        data.apiParameter = apiParameter;
      }

      if (module.hasPlugins || module.plugins.length) {
        data.plugins = module.plugins.map((plugin) => ({
          name: plugin.name,
          type: plugin.type,
          data: {
            ...plugin.hidden,
            ...mergeWithOriginals(plugin.values, plugin.original)
          }
        }));
      }

      config[module.id] = data;
    });

    return config;
  }, [mergeWithOriginals]);

  /**
   * updateModules
   * every mutation goes through here so dirty tracking stays in one place
   * @param mutate
   */
  const updateModules = (mutate: (current: ModuleEntry[]) => ModuleEntry[]) => {
    setModules((current) => mutate(current));
    setDirty(true);
    setMessage(null);
  };

  /**
   * updateModule
   * @param moduleId
   * @param mutate
   */
  const updateModule = (moduleId: string, mutate: (module: ModuleEntry) => ModuleEntry) => {
    updateModules((current) => current.map((module) => module.id === moduleId ? mutate(module) : module));
  };

  /**
   * handleStateChange
   * @param moduleId
   * @param state
   */
  const handleStateChange = (moduleId: string, state: string) => {
    updateModule(moduleId, (module) => {
      const data = { ...module.data };
      if (state) {
        data.state = state;
      } else {
        delete data.state;
      }
      return { ...module, data };
    });
  };

  /**
   * handleFlagChange
   * toggles nested enabled flags like price.enabled or productSearch.enabled
   * @param moduleId
   * @param node
   * @param enabled
   */
  const handleFlagChange = (moduleId: string, node: string, enabled: boolean) => {
    updateModule(moduleId, (module) => ({
      ...module,
      data: {
        ...module.data,
        [node]: { ...(module.data[node] || {}), enabled }
      }
    }));
  };

  /**
   * handleApiValueChange
   * @param moduleId
   * @param key
   * @param value
   */
  const handleApiValueChange = (moduleId: string, key: string, value: string) => {
    updateModule(moduleId, (module) => ({
      ...module,
      apiValues: { ...module.apiValues, [key]: value }
    }));
  };

  /**
   * handlePluginValueChange
   * @param moduleId
   * @param index
   * @param key
   * @param value
   */
  const handlePluginValueChange = (moduleId: string, index: number, key: string, value: string) => {
    updateModule(moduleId, (module) => ({
      ...module,
      plugins: module.plugins.map((plugin, pluginIndex) => pluginIndex === index
        ? { ...plugin, values: { ...plugin.values, [key]: value } }
        : plugin)
    }));
  };

  /**
   * handlePluginRemove
   * @param moduleId
   * @param index
   */
  const handlePluginRemove = (moduleId: string, index: number) => {
    updateModule(moduleId, (module) => ({
      ...module,
      plugins: module.plugins.filter((_plugin, pluginIndex) => pluginIndex !== index)
    }));
  };

  /**
   * handlePluginAdd
   * @param moduleId
   */
  const handlePluginAdd = (moduleId: string) => {
    const selection = pluginToAdd[moduleId];
    if (!selection) {
      return;
    }
    const [type, name] = selection.split("::"),
      entry = catalogEntry(type, name);
    if (!entry) {
      return;
    }

    const values: Record<string, string> = {};
    entry.parameter.forEach((key) => {
      values[key] = "";
    });

    updateModule(moduleId, (module) => ({
      ...module,
      plugins: [...module.plugins, { name, type, values, hidden: {}, original: {} }]
    }));
    setPluginToAdd((current) => ({ ...current, [moduleId]: "" }));
  };

  /**
   * handleModuleRemove
   * @param moduleId
   */
  const handleModuleRemove = (moduleId: string) => {
    updateModules((current) => current.filter((module) => module.id !== moduleId));
  };

  /**
   * handleModuleAdd
   */
  const handleModuleAdd = () => {
    if (!moduleToAdd) {
      return;
    }
    const listed = apiParameterList[moduleToAdd] || [],
      apiValues: Record<string, string> = {};
    listed.forEach((key) => {
      apiValues[key] = "";
    });

    updateModules((current) => [
      ...current,
      {
        id: moduleToAdd,
        data: { state: "inactive" },
        apiValues,
        apiOriginal: {},
        apiHidden: {},
        hasApiParameter: false,
        hasPlugins: false,
        plugins: []
      }
    ]);
    setModuleToAdd("");
  };

  /**
   * handleSave
   */
  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      // the backend expects config as a JSON string, exactly like store()
      const updated = await servicePartnerPartner.update(partner.id, {
        config: JSON.stringify(composeConfig(modules))
      } as unknown as ModelPartnerPartner);

      setDirty(false);
      setMessage({
        kind: "success",
        text: I18N.t("Die Konfiguration wurde gespeichert.")
      });
      if (onSaved) {
        onSaved(updated);
      }
    } catch (error: any) {
      const code = error?.response?.status;
      let text = I18N.t("Die Konfiguration konnte nicht gespeichert werden.");

      if (code === 403) {
        text = I18N.t("Nur der Lieferant darf die Konfiguration bearbeiten.");
      } else if (code === 422) {
        text = I18N.t("Die Konfiguration wurde vom Server nicht akzeptiert.");
      }

      setMessage({ kind: "error", text });
    } finally {
      setSaving(false);
    }
  };

  /**
   * variables
   */
  const availableModules = moduleTypeEnum.filter(
      (_type: any) => !modules.some((module) => module.id === _type.id)
    ),
    previewConfig = composeConfig(modules);

  /**
   * renderFlag
   * @param module
   * @param node
   * @param label
   */
  const renderFlag = (module: ModuleEntry, node: string, label: string) => {
    const flagId = `partner-config-${module.id}-${node}`;

    return (
      <dia-form-control>
        <label htmlFor={ flagId }>{ label }</label>
        <dia-switch>
          <input
            type="checkbox"
            id={ flagId }
            checked={ module.data[node]?.enabled === true }
            disabled={ !editable }
            onChange={ (event) => handleFlagChange(module.id, node, event.target.checked) }
          />
          <label htmlFor={ flagId }></label>
        </dia-switch>
      </dia-form-control>
    );
  };

  /**
   * renderTextField
   * one styled text input; the field labels are the raw technical parameter
   * names the classes announce, so they are shown untranslated
   * @param id
   * @param label
   * @param value
   * @param onChange
   */
  const renderTextField = (id: string, label: string, value: string, onChange: (value: string) => void) => (
    <dia-form-control>
      <input
        type="text"
        id={ id }
        name={ id }
        placeholder={ label }
        value={ value }
        readOnly={ !editable }
        onChange={ (event) => onChange(event.target.value) }
      />
      <label htmlFor={ id }>{ label }</label>
    </dia-form-control>
  );

  /**
   * renderApiParameter
   * fixed fields from the supplier api class's parameter list; unlisted
   * stored parameters stay invisible but survive saving
   * @param module
   */
  const renderApiParameter = (module: ModuleEntry) => {
    const listed = apiParameterList[module.id] || [],
      hiddenCount = Object.keys(module.apiHidden).length;

    if (!listed.length && !hiddenCount) {
      return null;
    }

    return (
      <div data-partner-config-api-parameter>
        <h4>{ I18N.t("API Parameter") }</h4>
        { listed.map((key) => (
          <dia-form-line key={ key }>
            { renderTextField(
              `partner-config-${module.id}-api-${key}`,
              key,
              module.apiValues[key] ?? "",
              (value) => handleApiValueChange(module.id, key, value)
            ) }
          </dia-form-line>
        )) }
        { hiddenCount > 0 && (
          <p data-partner-config-hint>
            { I18N.t("Von der Schnittstelle nicht gelistete Parameter bleiben unverändert erhalten:") } { hiddenCount }
          </p>
        ) }
      </div>
    );
  };

  /**
   * renderPlugins
   * @param module
   */
  const renderPlugins = (module: ModuleEntry) => {
    const addable = pluginCatalog.filter((entry) => entry.interlinkModulType === module.id);

    if (!module.plugins.length && (!editable || !addable.length)) {
      return null;
    }

    return (
      <div data-partner-config-plugins>
        <h4>{ I18N.t("Plugins") }</h4>
        { module.plugins.map((plugin, index) => {
          const entry = catalogEntry(plugin.type, plugin.name),
            hiddenCount = Object.keys(plugin.hidden).length;

          return (
            <div key={ `${plugin.type}-${plugin.name}-${index}` } data-partner-config-plugin>
              <p data-partner-config-plugin-title title={ entry?.descriptionLong || "" }>
                <strong>{ entry?.descriptionShort || plugin.name }</strong>
                <span> ({ pluginTypeLabel(plugin.type) })</span>
              </p>
              { Object.keys(plugin.values).length > 0 && (
                <dia-form-line>
                  { Object.keys(plugin.values).map((key) => renderTextField(
                    `partner-config-${module.id}-plugin-${index}-${key}`,
                    key,
                    plugin.values[key],
                    (value) => handlePluginValueChange(module.id, index, key, value)
                  )) }
                </dia-form-line>
              ) }
              { hiddenCount > 0 && (
                <p data-partner-config-hint>
                  { I18N.t("Nicht gelistete Plugin-Daten bleiben unverändert erhalten:") } { hiddenCount }
                </p>
              ) }
              { editable && (
                <button
                  type="button"
                  data-secondary
                  data-small
                  onClick={ () => handlePluginRemove(module.id, index) }
                >
                  <i data-icon-before="trash"></i>
                  { I18N.t("Plugin entfernen") }
                </button>
              ) }
            </div>
          );
        }) }
        { editable && addable.length > 0 && (
          <dia-form-line data-partner-config-add>
            <dia-form-control>
              <select
                id={ `partner-config-${module.id}-plugin-add` }
                name={ `partner-config-${module.id}-plugin-add` }
                value={ pluginToAdd[module.id] || "" }
                onChange={ (event) => setPluginToAdd((current) => ({ ...current, [module.id]: event.target.value })) }
              >
                <option value="" disabled hidden>{ I18N.t("Plugin wählen") }</option>
                { addable.map((entry) => (
                  <option key={ `${entry.type}::${entry.name}` } value={ `${entry.type}::${entry.name}` }>
                    { entry.descriptionShort } ({ pluginTypeLabel(entry.type) })
                  </option>
                )) }
              </select>
              <label htmlFor={ `partner-config-${module.id}-plugin-add` }>{ I18N.t("Plugin hinzufügen") }</label>
            </dia-form-control>
            <button
              type="button"
              data-secondary
              data-small
              disabled={ !pluginToAdd[module.id] }
              onClick={ () => handlePluginAdd(module.id) }
            >
              <i data-icon-before="plus"></i>
              { I18N.t("Hinzufügen") }
            </button>
          </dia-form-line>
        ) }
      </div>
    );
  };

  /**
   * renderModule
   * @param module
   */
  const renderModule = (module: ModuleEntry) => {
    const stateId = `partner-config-${module.id}-state`;

    return (
      <fieldset key={ module.id } data-partner-config-module>
        <legend>
          { moduleLabel(module.id) }
        </legend>

        <dia-form-line>
          <dia-form-control>
            { /* the bundle floats the label only over a disabled+hidden
                 placeholder option; the backend treats a missing state as
                 "inactive" anyway, so there is no selectable empty entry */ }
            <select
              id={ stateId }
              name={ stateId }
              value={ module.data.state || "" }
              disabled={ !editable }
              onChange={ (event) => handleStateChange(module.id, event.target.value) }
            >
              <option value="" disabled hidden>{ I18N.t("Status") }</option>
              { statusEnum.map((_state: any) => (
                <option key={ _state.id } value={ _state.id }>
                  { I18N.t(cleanLabel(_state.description, _state.id)) }
                </option>
              )) }
            </select>
            <label htmlFor={ stateId }>{ I18N.t("Status") }</label>
          </dia-form-control>
        </dia-form-line>

        { module.id === "priceAndStock" && (
          <dia-form-line>
            { renderFlag(module, "price", I18N.t("Preis aktiviert")) }
            { renderFlag(module, "stock", I18N.t("Bestand aktiviert")) }
          </dia-form-line>
        ) }

        { module.id === "productSearch" && (
          <dia-form-line>
            { renderFlag(module, "productSearch", I18N.t("Produktsuche aktiviert")) }
          </dia-form-line>
        ) }

        { TYPED_MODULES.includes(module.id) && renderApiParameter(module) }
        { TYPED_MODULES.includes(module.id) && renderPlugins(module) }

        { editable && (
          <button
            type="button"
            data-secondary
            data-small
            data-partner-config-module-remove
            onClick={ () => handleModuleRemove(module.id) }
          >
            <i data-icon-before="trash"></i>
            { I18N.t("Modul entfernen") }
          </button>
        ) }
      </fieldset>
    );
  };

  return (
    <dia-grid-nested data-grid-nested-full-width>
      <dia-grid-row data-grid-column-stretch>
        <dia-grid-column>
          <dia-panel data-fullwidth>
            <dia-panel-header>
              <IconComponent iconName="sliders" />
              { I18N.t("Konfiguration") }
            </dia-panel-header>
            <dia-panel-content>
              <dia-form>
                <form action="#" onSubmit={ (event) => event.preventDefault() }>
                  <dia-form-area>
                    { loaded && modules.length === 0 && (
                      <p data-partner-config-empty>{ I18N.t("Es sind keine Module konfiguriert.") }</p>
                    ) }
                    { modules.map((module) => renderModule(module)) }

                    { editable && loaded && availableModules.length > 0 && (
                      <dia-form-line data-partner-config-add>
                        <dia-form-control>
                          <select
                            id="partner-config-module-add"
                            name="partner-config-module-add"
                            value={ moduleToAdd }
                            onChange={ (event) => setModuleToAdd(event.target.value) }
                          >
                            <option value="" disabled hidden>{ I18N.t("Modul wählen") }</option>
                            { availableModules.map((_type: any) => (
                              <option key={ _type.id } value={ _type.id }>
                                { I18N.t(cleanLabel(_type.description, _type.id)) }
                              </option>
                            )) }
                          </select>
                          <label htmlFor="partner-config-module-add">{ I18N.t("Modul hinzufügen") }</label>
                        </dia-form-control>
                        <button
                          type="button"
                          data-secondary
                          data-small
                          disabled={ !moduleToAdd }
                          onClick={ handleModuleAdd }
                        >
                          <i data-icon-before="plus"></i>
                          { I18N.t("Hinzufügen") }
                        </button>
                      </dia-form-line>
                    ) }
                  </dia-form-area>

                  <InlineMessage section="partner-config" message={ message } />

                  { editable && (
                    <button
                      type="button"
                      data-primary-dark
                      disabled={ saving || !dirty }
                      onClick={ handleSave }
                    >
                      <i data-icon-before="save"></i>
                      { saving ? I18N.t("Speichert …") : I18N.t("Speichern") }
                    </button>
                  ) }
                </form>
              </dia-form>
            </dia-panel-content>
          </dia-panel>
        </dia-grid-column>
      </dia-grid-row>
      <dia-grid-row data-grid-column-stretch>
        <dia-grid-column>
          <dia-panel data-fullwidth>
            <dia-panel-header>
              <IconComponent iconName="laptop-code" />
              { I18N.t("JSON Konfiguration") }
            </dia-panel-header>
            <dia-panel-content data-panel-no-padding>
              <textarea
                readOnly
                value={ JSON.stringify(previewConfig, null, 2) }
                has="code-mirror"
              ></textarea>
            </dia-panel-content>
          </dia-panel>
        </dia-grid-column>
      </dia-grid-row>
    </dia-grid-nested>
  );
};

export default PartnerConfigForm;
